import type {
  ActionPlanItem,
  AiInsightResponse,
  InsightBrief,
  SafeFoodItem,
  SuspectedTriggerItem,
  WeeklyInsight,
} from './types';
import { stripBriefForStorage } from './insightBrief';
import { applyFirmVerdicts, briefNames, guardHeadlineSummary } from './insightGuard';
import { normaliseIngredientName } from './ingredientTaxonomy';
import { getWeekDateRange } from './dateUtils';
import { displayFoodName } from './foodDisplayName';

type PlanStep = AiInsightResponse['plan'][number];

const PLAN_CATEGORY: Record<PlanStep['type'], ActionPlanItem['category']> = {
  cut: 'elimination',
  test: 'reintroduction',
  reintroduce: 'reintroduction',
  keep: 'maintenance',
};

export function briefOnlyReport(brief: InsightBrief): string {
  const lines: string[] = ['## Summary'];
  if (brief.gutScore) {
    lines.push(`Gut score ${brief.gutScore.score}/100 (${brief.gutScore.band}). ${brief.gutScore.reason}`);
  } else {
    lines.push('Not enough logged days yet to score your gut.');
  }
  if (brief.ingredientStats.length) {
    lines.push('', '### Possible triggers');
    for (const s of brief.ingredientStats.slice(0, 5)) {
      lines.push(`- ${s.name}: eaten ${s.timesEaten} times, followed by a flare ${s.timesFollowedByFlare} times (lift ${s.lift}, ${s.confidence} confidence)`);
    }
  }
  if (brief.safeIngredients.length) {
    lines.push('', '### Likely safe');
    for (const s of brief.safeIngredients.slice(0, 5)) {
      lines.push(`- ${s.name}: eaten ${s.timesEaten} times, lift ${s.lift}`);
    }
  }
  if (!brief.ingredientStats.length && !brief.safeIngredients.length) {
    lines.push('', 'Keep logging meals and symptoms to reveal patterns.');
  }
  return lines.join('\n');
}

/** Template headline and summary shown (marked "Calculated") when the AI is unavailable. */
export function briefOnlySummary(brief: InsightBrief): { headline: string; summary: string } {
  const g = brief.gutScore;
  const headline = !g
    ? 'Not enough data yet.'
    : g.band === 'STABLE'
    ? 'A settled week.'
    : g.band === 'SENSITIVE'
    ? 'A mixed week.'
    : 'A rough week.';
  const parts: string[] = [];
  parts.push(g ? `Your gut score is ${g.score}/100.` : 'Log a few more days to get a gut score.');
  const top = brief.ingredientStats.find((s) => s.confidence !== 'low');
  if (top) {
    parts.push(`${displayFoodName(top.name)} was followed by a flare ${top.timesFollowedByFlare} of ${top.timesEaten} times.`);
  } else {
    parts.push('No clear trigger stands out yet.');
  }
  return { headline, summary: parts.join(' ') };
}

/** A brief-only "cut" needs a clear signal: lift of at least this much. Weaker leads get a "test" step. */
export const CUT_MIN_LIFT = 1.5;

/** Steps that do not name a food, used last to keep the plan at three distinct steps. */
const GENERIC_STEPS: PlanStep[] = [
  {
    type: 'keep',
    title: 'Keep logging',
    items: [],
    detail: 'Log every meal and symptom this week so the next report can find clearer patterns.',
  },
  {
    type: 'keep',
    title: 'Log bowel movements',
    items: [],
    detail: 'Log each bowel movement with its stool type so the next report can follow your bowel habit.',
  },
  {
    type: 'keep',
    title: 'Note portion sizes',
    items: [],
    detail: 'Note roughly how much of each food you ate so the next report can compare amounts.',
  },
];

/** Plan steps derived from the brief alone, best first (more than three; the caller picks). */
function briefPlanPool(brief: InsightBrief): PlanStep[] {
  const pool: PlanStep[] = [];
  const suspects = brief.ingredientStats.filter((s) => s.confidence === 'high' || s.confidence === 'medium');
  suspects.slice(0, 2).forEach((s, i) => {
    if (i === 0 && s.lift >= CUT_MIN_LIFT) {
      pool.push({
        type: 'cut',
        title: `Pause ${s.name}`,
        items: [s.name],
        detail: `Leave ${s.name} out for a week and note whether symptoms ease. It was followed by a flare ${s.timesFollowedByFlare} of ${s.timesEaten} times.`,
      });
    } else {
      pool.push({
        type: 'test',
        title: `Test ${s.name}`,
        items: [s.name],
        detail: `Eat one normal portion of ${s.name} on its own and watch how you feel over the next day.`,
      });
    }
  });
  for (const safe of brief.safeIngredients) {
    pool.push({
      type: 'keep',
      title: `Keep ${safe.name}`,
      items: [safe.name],
      detail: `${safe.name} looks safe for you (eaten ${safe.timesEaten} times without extra flares). Keep eating it.`,
    });
  }
  return pool;
}

/**
 * The three plan steps shown and synced to Actions. AI steps survive only when every item is a brief
 * name (the guard's name set) and a "cut" step does not name a lead whose final verdict is reject
 * (the AI review after firm verdicts, or the engine's firm verdicts when there is no AI). The plan is
 * padded back to three from the brief-only steps, then generic steps, never repeating a title or item.
 */
export function finalPlan(brief: InsightBrief, ai: AiInsightResponse | null): PlanStep[] {
  const norm = (n: string) => normaliseIngredientName(n);
  const names = new Set(briefNames(brief));
  const rejected = new Set<string>(
    ai
      ? ai.engineLeadReview.filter((l) => l.verdict === 'reject').map((l) => norm(l.name))
      : (brief.leadHints ?? []).filter((h) => h.firm === 'reject').map((h) => norm(h.name))
  );
  const valid = (p: PlanStep) =>
    p.items.every((i) => names.has(norm(i))) && !(p.type === 'cut' && p.items.some((i) => rejected.has(norm(i))));

  const plan: PlanStep[] = [];
  const titles = new Set<string>();
  const items = new Set<string>();
  const add = (p: PlanStep) => {
    if (plan.length >= 3 || !valid(p)) return;
    const title = p.title.trim().toLowerCase();
    if (titles.has(title) || p.items.some((i) => items.has(norm(i)))) return;
    plan.push(p);
    titles.add(title);
    p.items.forEach((i) => items.add(norm(i)));
  };
  (ai?.plan ?? []).forEach(add);
  briefPlanPool(brief).forEach(add);
  GENERIC_STEPS.forEach(add);
  return plan;
}

/** The plan used when the AI is unavailable ("Calculated"). */
export function briefOnlyPlan(brief: InsightBrief): AiInsightResponse['plan'] {
  return finalPlan(brief, null);
}

export function assembleInsight(
  brief: InsightBrief,
  ai: AiInsightResponse | null,
  provider: string,
  nowIso: string,
): WeeklyInsight {
  if (ai) ai = guardHeadlineSummary(applyFirmVerdicts(ai, brief), brief).ai;
  const triggers: SuspectedTriggerItem[] = brief.ingredientStats.map((s, i) => ({
    id: `trig_${i}`,
    ingredient_name: s.name,
    confidence: s.confidence.toUpperCase() as SuspectedTriggerItem['confidence'],
    lag_window: s.typicalLagHours === null ? '' : `~${s.typicalLagHours}h lag`,
    transit_zone: '',
    primary_symptoms: s.mainSymptoms,
    implicated_meals: [],
    mechanism: `Flared ${s.lift}× your usual over ${s.timesEaten} meals`,
  }));
  const safe: SafeFoodItem[] = brief.safeIngredients.map((s) => ({
    name: s.name,
    sessions_count: s.timesEaten,
    category: 'Ingredient',
  }));
  const plan: ActionPlanItem[] = finalPlan(brief, ai).map((p, i) => ({
    id: `plan_${i}`,
    title: p.title,
    description: p.detail,
    category: PLAN_CATEGORY[p.type],
    target_triggers: p.items,
    avoid_ingredients: p.type === 'cut' ? p.items : [],
    status: 'pending',
  }));

  const insight: WeeklyInsight = {
    generated_at_iso: nowIso,
    week_range: getWeekDateRange(7),
    ai_summary_markdown: ai?.full_report ?? briefOnlyReport(brief),
    suspected_triggers: JSON.stringify(triggers),
    safe_foods: JSON.stringify(safe),
    suspected_triggers_data: triggers,
    safe_foods_data: safe,
    action_plans: plan,
    format_version: 2,
    brief: stripBriefForStorage(brief),
    ai,
    provider,
  };
  if (brief.gutScore) insight.health_score = brief.gutScore.score;
  return insight;
}
